import { useEffect } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Input } from '@repo/videa-components/src/ui/input';
import { Label } from '@repo/videa-components/src/ui/label';
import { useFormContext } from 'react-hook-form';

import { useReferralDetailsByReferralId } from '@/react-query/useReferralInfo';

import type { ReferralFormValues } from '../../types';

import { DateOfBirthField } from './DateOfBirthField';
import { PhoneInputField } from './PhoneInputField';

interface PatientAndDoctorTabProps {
  referralId?: string;
}

export const PatientAndDoctorTab = ({
  referralId,
}: PatientAndDoctorTabProps = {}) => {
  const { t } = useLingui();
  const {
    register,
    formState: { errors, defaultValues },
    setValue,
    getValues,
  } = useFormContext<ReferralFormValues>();

  // Fetch referral details by referralId if available
  const { data: referralDetails } = useReferralDetailsByReferralId(
    referralId || '',
    {
      enabled: !!referralId,
    },
  );

  // Prefill fields with data from referralDetails if present and not already filled
  useEffect(() => {
    if (!referralDetails) return;

    const currentPatientEmail = getValues('patientEmail');
    const currentPatientPhone = getValues('patientPhone');
    const currentPracticeEmail = getValues('practiceEmail');

    // Prefill patient email if available and not already set
    if (referralDetails.patient_email && !currentPatientEmail) {
      setValue('patientEmail', referralDetails.patient_email);
    }

    // Prefill patient phone if available and not already set
    // Priority: patient_phone > dentrix_home_phone > dentrix_work_phone
    if (!currentPatientPhone) {
      const phoneToUse =
        referralDetails.patient_phone ||
        referralDetails.dentrix_home_phone ||
        referralDetails.dentrix_work_phone;
      if (phoneToUse) {
        setValue('patientPhone', phoneToUse);
      }
    }

    // Prefill practice email from dentrix_contact if available and not already set
    if (
      referralDetails.dentrix_contact?.email &&
      !currentPracticeEmail &&
      !defaultValues?.practiceEmail
    ) {
      setValue('practiceEmail', referralDetails.dentrix_contact.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralDetails, setValue, getValues]);

  const emailPattern = {
    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: t`Please enter a valid email address`,
  };

  return (
    <div className="vai-space-y-6">
      {/* Patient Information Section */}
      <div>
        <h3 className="vai-font-medium vai-text-gray-500">
          {t`Patient Information`}
        </h3>
        <div className="vai-flex vai-items-center vai-gap-3 vai-mb-4">
          <div className="vai-flex-1 vai-h-[1px] vai-bg-gray-200" />
        </div>
        <div className="vai-grid vai-grid-cols-1 vai-gap-4 sm:vai-grid-cols-2">
          <div className="vai-space-y-1">
            <Label className="vai-text-sm">
              {t`Patient Name`}
              <span className="vai-text-red-500">*</span>
            </Label>
            <div className="vai-flex vai-gap-2">
              <Input
                id="patientFirstName"
                placeholder={t`First Name`}
                {...register('patientFirstName', { required: true })}
                className="vai-h-8 vai-text-sm vai-w-1/2"
                disabled={!!defaultValues?.patientFirstName}
              />
              <Input
                id="patientLastName"
                placeholder={t`Last Name`}
                {...register('patientLastName', { required: true })}
                className="vai-h-8 vai-text-sm vai-w-1/2"
                disabled={!!defaultValues?.patientLastName}
              />
            </div>
          </div>

          <DateOfBirthField />

          <div className="vai-space-y-1">
            <Label htmlFor="patientEmail" className="vai-text-sm">
              {t`Email`}{' '}
              <span className="vai-text-gray-500">
                <Trans>optional</Trans>
              </span>
            </Label>
            <Input
              id="patientEmail"
              type="email"
              {...register('patientEmail', {
                pattern: emailPattern,
              })}
              className={`vai-h-8 vai-text-sm ${
                errors.patientEmail ? 'vai-border-red-500' : ''
              }`}
            />
            {errors.patientEmail && (
              <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                {errors.patientEmail.message}
              </p>
            )}
          </div>

          <PhoneInputField
            name="patientPhone"
            label={t`Phone`}
            required
            placeholder={t`Enter patient phone number`}
          />
        </div>
      </div>

      {/* Referring Doctor Information Section */}
      <div>
        <h3 className="vai-font-medium vai-text-gray-500">
          {t`Referring Doctor Information`}
        </h3>
        <div className="vai-flex vai-items-center vai-gap-3 vai-mb-4">
          <div className="vai-flex-1 vai-h-[1px] vai-bg-gray-200" />
        </div>
        <div className="vai-grid vai-grid-cols-1 vai-gap-4 sm:vai-grid-cols-2">
          <div className="vai-space-y-1">
            <Label className="vai-text-sm">
              {t`Doctor Name`}
              <span className="vai-text-red-500">*</span>
            </Label>
            <div className="vai-flex vai-gap-2">
              <Input
                id="doctorFirstName"
                placeholder={t`First Name`}
                {...register('doctorFirstName', { required: true })}
                className="vai-h-8 vai-text-sm vai-w-1/2"
              />
              <Input
                id="doctorLastName"
                placeholder={t`Last Name`}
                {...register('doctorLastName', { required: true })}
                className="vai-h-8 vai-text-sm vai-w-1/2"
              />
            </div>
          </div>

          <div className="vai-space-y-1">
            <Label htmlFor="practiceEmail" className="vai-text-sm">
              {t`Practice Email`}
              <span className="vai-text-red-500">*</span>
            </Label>
            <Input
              id="practiceEmail"
              type="email"
              {...register('practiceEmail', {
                required: t`Practice email is required`,
                pattern: emailPattern,
              })}
              className={`vai-h-8 vai-text-sm ${
                errors.practiceEmail ? 'vai-border-red-500' : ''
              }`}
              disabled={!!defaultValues?.practiceEmail}
            />
            {errors.practiceEmail && (
              <p className="vai-text-red-500 vai-text-xs vai-mt-1">
                {errors.practiceEmail.message}
              </p>
            )}
          </div>

          <PhoneInputField
            name="doctorPhone"
            label={t`Practice Phone`}
            required
            disabled={!!defaultValues?.doctorPhone}
            placeholder={t`Enter practice phone number`}
            defaultCountry="CA"
          />
        </div>
      </div>
    </div>
  );
};
